import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';
import { compose, forEachObjIndexed, includes, map, uniq } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';

import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { addSingleErrorByPageAndId, resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import { radioOptionsIds, searchUserTypes } from 'features/other/resolverSettings/constants';
import { getUserDataFromDBFlag } from 'features/other/resolverSettings/selectors';
// eslint-disable-next-line import/no-cycle
import typeTasks from 'features/other/typeTasks';
// eslint-disable-next-line import/no-cycle
import taskOverview from 'features/views/taskOverview';
// eslint-disable-next-line import/no-cycle
import taskSearch from 'features/views/taskSearch';
import { history, toIsoDateFormat } from 'utils';

import { isOnTaskDetail } from '../taskDetail/utils';
import userSearch from '../userSearch';
import * as types from './actionTypes';
import { dialogs, TASK_FORWARDING_ALREADY_PROCESSED, TASK_FORWARDING_HAS_NO_ACCESS_ERR_MSG } from './constants';
import { TASK_FORWARDING_FORM_NAME } from './form';
import * as selectors from './selectors';

// tasks
export const setTasks = createAction(types.SET_TASKS, (tasks) => ({ tasks }));
export const removeTask = createAction(types.REMOVE_TASKS, (id) => ({ id }));
export const resetTask = createAction(types.RESET_TASKS);

export const tryRedirectTask =
	() =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const formData = await getFormData(TASK_FORWARDING_FORM_NAME);
		const userDatafromDB = getUserDataFromDBFlag(getState());

		// close and reset all the "required" errors
		dispatch(dialog.actions.close(TASK_FORWARDING_FORM_NAME));
		dispatch(resetErrorsByPage({ page: TASK_FORWARDING_FORM_NAME }));

		if (formData?.radio === radioOptionsIds.users && !userDatafromDB) {
			const result = await dispatch(
				userSearch.actions.searchUser({
					type: searchUserTypes.USER_LIST_SEARCH,
					formData: {
						login: formData?.login,
						firstName: formData?.userFirstName,
						surname: formData?.userSurname,
						sellerIn: formData?.sellerIn,
					},
				}),
			);

			const data = result?.body?.data;

			if (result && !isNilOrEmpty(data) && data.length === 1) {
				const { login, sellerIn, firstName, surname } = data[0];

				const fieldsToUpdateResolverSettings = {
					login,
					sellerIn,
					userFirstName: firstName,
					userSurname: surname,
				};

				forEachObjIndexed((value, key) => {
					sendControlNext(TASK_FORWARDING_FORM_NAME, setValue({ fieldName: key, value }));
				}, fieldsToUpdateResolverSettings);
				dispatch(dialog.actions.close(TASK_FORWARDING_FORM_NAME));
			} else {
				dispatch(
					addSingleErrorByPageAndId({
						page: TASK_FORWARDING_FORM_NAME,
						errors: ['nonUniqueSolver'],
					}),
				);
				return dispatch(dialog.actions.open(TASK_FORWARDING_FORM_NAME));
			}
		}

		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('lightBox:headerSendAssignment'),
			onConfirm: () => dispatch(redirectTask()),
		});
	};

export const redirectTask =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(TASK_FORWARDING_FORM_NAME);
		const tasks = selectors.getTasks(getState());

		let destination;

		switch (formData.radio) {
			case radioOptionsIds.subordinate:
				destination = formData.subordinate;
				break;
			case radioOptionsIds.superior:
				destination = formData.superior;
				break;
			case radioOptionsIds.agency:
				destination = formData.agency;
				break;
			case radioOptionsIds.users:
				destination = formData.login;
				break;
		}

		const body = {
			destination,
			tasks: tasks.map((task) => ({ id: task.id })),
		};

		if (formData.deadlineDate) body.deadlineDate = toIsoDateFormat(formData.deadlineDate);

		const handleReloadBasedOnCurrentLocation = () => {
			if (isOnTaskDetail()) {
				history.go(-1);
			}

			if (window.location.pathname === '/tasks') {
				dispatch(taskOverview.actions.reload());

				const taskTypeIds = compose(
					uniq,
					map((task) => task.taskTypeId),
				)(tasks);

				taskTypeIds.map((item) => dispatch(typeTasks.actions.reloadCurrentPage(item)));
			}

			if (window.location.pathname === '/task/search') {
				dispatch(taskSearch.actions.reload());
			}

			NiceModal.hide(MODALS.task_forwarding);

			dispatch(typeTasks.actions.clearSelectTasks());
		};

		dispatch(
			fetch.actions.post(fetch.constants.urls.taskRedirect, {
				body,
				onSuccess: [
					() => () => {
						enqueueSnackbar(getLabelMessage('toastr:approveTaskRedirect'), { variant: 'success' });
						handleReloadBasedOnCurrentLocation();
					},
				],
				onError: [
					({ failure }) =>
						() => {
							if (failure?.status === 400) {
								dispatch(dialog.actions.open(dialogs.validation_error, null, dialog.constants.ERROR));

								if (failure?.parsedContent?.code === TASK_FORWARDING_ALREADY_PROCESSED) {
									enqueueSnackbar(getLabelMessage('toastr:taskRedirectErrorAlreadyProcessed'), {
										variant: 'error',
									});
									handleReloadBasedOnCurrentLocation();
								}
							}

							if (
								failure?.status === 403 &&
								includes(TASK_FORWARDING_HAS_NO_ACCESS_ERR_MSG, failure?.parsedContent?.message)
							) {
								enqueueSnackbar(getLabelMessage('toastr:taskRedirectErrorUserNotResolver'), {
									variant: 'error',
								});
								handleReloadBasedOnCurrentLocation();
							}
						},
				],
				shouldReportError: ({ failure }) => {
					return !includes(failure?.status, [400, 403]);
				},
			}),
		);
	};
